import cleanDeep from "clean-deep";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import printjs from "print-js";

import { BRLMoneyToNumber } from "utils";

import api from "../index";

type Product = "aluguel" | "imobiliaria" | "essencial";

function formatDate(date: string) {
  return dayjs(date).format("YYYY-MM-DD");
}

function operationCodeParser(percentage) {
  return (
    {
      "1": "1",
      "2": "2",
      "3": "3",
      "4": "4",
      "5": "5",
      "6": "6",
      "7": "7",
      "8": "8",
      "9": "9",
      "10": "10",
      "11": "11",
      "12": "12",
      "13": "13",
      "14": "14",
      "15": "15",
      "16": "16",
      "17": "17",
      "18": "18",
      "19": "19",
      "20": "20",
      "21": "154",
      "22": "155",
      "23": "156",
      "24": "157",
      "25": "158",
      "26": "159",
      "27": "160",
      "28": "161",
      "29": "162",
      "30": "163",
      "31": "164",
      "32": "165",
      "33": "166",
      "34": "167",
      "35": "168",
      "36": "169",
      "37": "170",
      "38": "171",
      "39": "172",
      "40": "173",
      "41": "174",
      "42": "175",
      "43": "176",
      "44": "177",
      "45": "178",
      "46": "179",
      "47": "180",
      "48": "181",
      "49": "182",
      "50": "183",
      "51": "184",
      "52": "185",
      "53": "186",
      "54": "187",
      "55": "188",
      "56": "189",
      "57": "190",
      "58": "191",
      "59": "192",
      "60": "193",
      "61": "194",
      "62": "195",
      "63": "196",
      "64": "197",
      "65": "198",
      "66": "199",
      "67": "200",
      "68": "201",
      "69": "202",
      "70": "203",
    }[percentage] || percentage
  );
}

function contractParse(type: Product, payload) {
  const {
    partner,
    susep,
    operation_code,
    contract_plan,
    contract_commission,
    estate_type,
    estate_address,
    coverages,
  } = payload;

  return cleanDeep({
    product: {
      aluguel: "PORTO_ALUGUEL",
      essencial: "ESSENCIAL",
      imobiliaria: "PORTO_IMOBILIARIA",
    }[type],
    ...{
      aluguel: () => {
        const customerParse = ({ birthdate, employment, ...customer }) => {
          customer.id && delete customer.id;

          return {
            ...customer,
            birthdate: birthdate.split("/").reverse().join("-"),
            employment: {
              ...employment,
              salary: BRLMoneyToNumber(employment.salary),
              incomes: BRLMoneyToNumber(employment.incomes),
            },
          };
        };

        return {
          broker: susep,
          partner: partner,
          payload: {
            contract: {
              plan: contract_plan.toUpperCase(),
              operation_code: operation_code,
              readjustment: payload.estate_readjustmentIndex,
              period: {
                start: formatDate(payload.contract_period_start),
                end: formatDate(payload.contract_period_end),
              },
            },
            customers: [
              {
                ...customerParse(payload.suitors_main),
                financial: true,
              },
              ...payload.suitors_additionals.map((customer) =>
                customerParse(customer)
              ),
            ],
            estate: {
              type: estate_type,
              reason: payload.estate_reason,
              address: estate_address,
            },
            coverages: {
              ...coverages,
              ...payload.coverages_additionals,
            },
          },
        };
      },
      essencial: () => {
        return {
          broker: organization_susep,
          partner: {
            broker: organization_partner,
            realEstate: organization_code,
          }[organization_type],
          payload: {
            customers: payload.customers,
            address: payload.address,
            rent_price: BRLMoneyToNumber(payload.rent_price),
          },
        };
      },
      imobiliaria: () => {
        return {
          broker: susep,
          partner: partner,
          payload: {
            contract: {
              plan: contract_plan.toUpperCase(),
              // discount: Number(payload.contract_discount.replace('%', '')),
              period: {
                start: formatDate(payload.contract_period_start),
                end: formatDate(payload.contract_period_end),
              },
              service_clause: payload.contract_clause,
              operation_code: operationCodeParser(contract_commission),
            },
            customer: {
              document: payload.customer_document,
              name: payload.customer_name,
              email: payload.customer_email,
              phone: payload.customer_phone,
            },
            estate: {
              type: estate_type,
              address: estate_address,
              owner: payload.estate_owner,
            },
            coverages,
          },
        };
      },
    }[type](),
  });
}

export async function create(product: Product, payload) {
  try {
    const response = await api.instance.post(
      "https://api-dot-porto-seguro-r-financeiros-stg.uc.r.appspot.com/contracts",
      contractParse(product, payload)
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function update(id: string, payload) {
  const payloadParse = {
    ...payload,
    customers: payload.customers.map(({ id, ...customer }) => ({
      ...customer,
      birthdate: customer.birthdate.split("/").reverse().join("-"),
    })),
    landlord: {
      ...payload.landlord,
      birthdate: payload.landlord.birthdate.split("/").reverse().join("-"),
    },
    contract: {
      ...payload.contract,
      period: {
        ...Object.entries(payload.contract.period).reduce(
          (prev, [key, value]: any) => {
            return {
              ...prev,
              [key]: formatDate(value),
            };
          },
          {}
        ),
      },
    },
  };

  try {
    const response = await api.instance.patch(
      `https://api-dot-porto-seguro-r-financeiros-stg.uc.r.appspot.com/contracts/${id}`,
      payloadParse
    );
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function get(id: string) {
  try {
    const response = await api.instance.get(
      `https://api-dot-porto-seguro-r-financeiros-stg.uc.r.appspot.com/contracts/${id}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function order(id: string, payload) {
  try {
    const response = await api.instance.post(
      `https://api-dot-porto-seguro-r-financeiros-stg.uc.r.appspot.com/contracts/${id}/orders`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function print(id: string, type: "proposal" | "letter") {
  try {
    const { data: pdf } = await api.instance.get(`/contracts/${id}/pdf`, {
      responseType: "arraybuffer",
      params: {
        type: type.toUpperCase(),
      },
    });

    const base64 = Buffer.from(pdf, "binary").toString("base64");

    printjs({
      printable: base64,
      showModal: true,
      base64: true,
      type: "pdf",
      onError: () => {
        const blob = new Blob([pdf.data], {
          type: "application/pdf",
        });
        saveAs(blob, `${id}.pdf`);
      },
    });
  } catch (error) {
    throw error;
  }
}
